import React, { useCallback, useEffect, useState } from "react";
import API_BASE from "./apiBase";
import { displayName, useMaskNames } from "./nameMask";

/**
 * MESSAGING - the customer-message templates and which location sends which.
 *
 * A message is a subject and a body with placeholders: {customer}, {branch},
 * {parcel}, {location}, {driver}, {tracking_link}. Attach it to a location,
 * and every opted-in customer whose parcel arrives there gets it. A driver's
 * location (a tote, a van) makes {tracking_link} a live map of that driver;
 * the server adds the link if the message forgot it.
 */
const token = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";
async function api(path, method = "GET", body) {
  const res = await fetch(`${API_BASE}/api/messaging${path}`, {
    method, headers: { Authorization: `Bearer ${token()}`, "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d?.error?.message || (typeof d?.error === "string" ? d.error : null) || `HTTP ${res.status}`);
  return d;
}
const S = {
  panel: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, marginBottom: 14 },
  h: { fontSize: 15, fontWeight: 800, color: "#111827", marginBottom: 4 },
  muted: { fontSize: 12, color: "#6b7280" },
  th: { textAlign: "left", fontSize: 11, fontWeight: 800, color: "#6b7280", padding: "6px 8px", borderBottom: "2px solid #e5e7eb", textTransform: "uppercase" },
  td: { fontSize: 13, padding: "6px 8px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" },
  inp: { border: "1px solid #d1d5db", borderRadius: 4, padding: "5px 8px", fontSize: 13, width: "100%", boxSizing: "border-box" },
  btn: (c, solid) => ({ background: solid ? c : "#fff", color: solid ? "#fff" : c, border: `1px solid ${c}`, borderRadius: 5, padding: "5px 11px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }),
  error: { color: "#b91c1c", fontSize: 12.5, marginTop: 6 },
};
const EMPTY = { name: "", subject: "", body: "" };

export default function MessagingPanel() {
  const masked = useMaskNames();
  const [messages, setMessages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, name, subject, body}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, l] = await Promise.all([api("/messages"), api("/location-links")]);
      setMessages(m.messages || []); setLocations(l.locations || []); setErr(null);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      if (editing.id) await api(`/messages/${editing.id}`, "PUT", editing);
      else await api("/messages", "POST", editing);
      setEditing(null); await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? Locations using it will stop sending it.`)) return;
    try { await api(`/messages/${m.id}`, "DELETE"); await load(); } catch (e) { setErr(e.message); }
  };
  const link = async (loc, messageId) => {
    try { await api(`/location-links/${loc.id}`, "PUT", { message_id: messageId || null }); await load(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <>
      <div style={S.panel}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={S.h}>Messages to customers</div>
            <div style={S.muted}>
              Sent by email when a parcel arrives at a location that has the message attached, to customers who have
              ticked "notifications and updates". Placeholders: {"{customer} {branch} {parcel} {location} {driver} {tracking_link}"}.
            </div>
          </div>
          <button style={S.btn("#1976d2", true)} onClick={() => setEditing({ ...EMPTY })}>New message</button>
        </div>
        {err && <div style={S.error}>{err}</div>}
        {editing && (
          <div style={{ marginTop: 10, padding: 10, background: "#eff6ff", border: "1px solid #99f6e4", borderRadius: 6 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8, marginBottom: 8 }}>
              <input style={S.inp} placeholder="Name (for the office)" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <input style={S.inp} placeholder="Email subject, e.g. Your parcel from {branch} is on its way" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
            </div>
            <textarea rows={5} style={{ ...S.inp, fontFamily: "inherit", resize: "vertical" }}
              placeholder={"Hello {customer},\n\nYour parcel ({parcel}) is with {driver} and on its way to you.\n\nFollow it here: {tracking_link}"}
              value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
              <button style={S.btn("#6b7280")} disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
              <button style={S.btn("#1976d2", true)} disabled={busy || !editing.name.trim() || !editing.subject.trim() || !editing.body.trim()} onClick={save}>
                {busy ? "Saving…" : "Save message"}
              </button>
            </div>
          </div>
        )}
        {messages.length === 0 && !editing && <div style={{ ...S.muted, marginTop: 8 }}>No messages yet.</div>}
        {messages.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
            <thead><tr><th style={S.th}>Name</th><th style={S.th}>Subject</th><th style={S.th}>Used by</th><th style={S.th}></th></tr></thead>
            <tbody>
              {messages.map((m) => {
                const users = locations.filter((l) => l.message_id === m.id);
                return (
                  <tr key={m.id}>
                    <td style={{ ...S.td, fontWeight: 700 }}>{m.name}</td>
                    <td style={S.td}>{m.subject}<div style={S.muted}>{m.body.slice(0, 120)}{m.body.length > 120 ? "…" : ""}</div></td>
                    <td style={S.td}>{users.length ? `${users.length} location${users.length === 1 ? "" : "s"}` : <span style={S.muted}>none</span>}</td>
                    <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button style={S.btn("#1976d2")} onClick={() => setEditing({ id: m.id, name: m.name, subject: m.subject, body: m.body })}>Edit</button>{" "}
                      <button style={S.btn("#b91c1c")} onClick={() => remove(m)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={S.panel}>
        <div style={S.h}>Which location sends which message</div>
        <div style={S.muted}>A parcel arriving at a location with a message attached emails the customer. A driver's tote or van also gives them a live map and arrival time.</div>
        {locations.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
            <thead><tr><th style={S.th}>Branch</th><th style={S.th}>Location</th><th style={S.th}>Type</th><th style={S.th}>Driver</th><th style={S.th}>Message</th></tr></thead>
            <tbody>
              {locations.map((l) => (
                <tr key={l.id}>
                  <td style={S.td}>{masked ? `Branch ${l.account_id}` : l.branch}</td>
                  <td style={{ ...S.td, fontWeight: 700 }}>{l.description}</td>
                  <td style={S.td}>{l.location_type}</td>
                  <td style={S.td}>{l.driver_name ? displayName(l.driver_name, masked) : <span style={S.muted}>—</span>}</td>
                  <td style={S.td}>
                    <select style={{ ...S.inp, width: 260 }} value={l.message_id || ""} onChange={(e) => link(l, e.target.value)}>
                      <option value="">No message</option>
                      {messages.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
